import React from 'react';
import PropTypes from 'prop-types';
import { LuFileText, LuPenLine, LuMonitorCheck } from 'react-icons/lu';
import { Field, SelectInput, TextArea, TextInput } from '../../../../components/form/FormControls';
import { useTranslation } from '../../../../i18n/LanguageContext';
import { normalizePatientId } from '../../../../utils/patientAccess';
import { VISIT_TYPES } from '../visitSteps';
import { FIELD_GRID, MissingTag, Notice, PANEL, ReadValue, SectionCard, YesNo, fieldAnchor } from '../VisitUi';

const ISSUE_CATEGORIES = ['iv_access', 'patient_compliance', 'documentation', 'logistics', 'protocol', 'equipment', 'other'];
const GRID_3 = 'tw-grid tw-grid-cols-1 tw-gap-4 sm:tw-grid-cols-3';

/**
 * Hospital (site) evaluation, sections A–F - used for every non-home visit
 * type, as before. D never blocks completion; what is missing there is shown
 * as pending until it is entered.
 */
const SiteEvaluation = ({ formData, isReadOnly, projectTitle, missingKeys, sections, cycleTime, slaMet, slaOverdue, pendingCompletionLabels, onEvalChange }) => {
  const { t } = useTranslation();
  const f = (key) => t(`visitModal.evaluation.fields.${key}`);
  const ev = formData.visitEvaluation || {};
  const yesNo = (value) => (value === null ? '-' : value ? t('visitModal.common.yes') : t('visitModal.common.no'));

  const section = (letter) => ({
    letter,
    title: t(`visitModal.evaluation.site.${letter}.title`),
    hint: t(`visitModal.evaluation.site.${letter}.hint`),
    missingCount: sections[letter]?.missing || 0,
    complete: sections[letter]?.total > 0 && !sections[letter]?.missing,
  });
  const req = (key) => ({ required: true, badge: missingKeys.has(key) ? <MissingTag /> : null });

  const visitTypeText = formData.visitType === 'hastane'
    ? f('siteVisitType')
    : VISIT_TYPES.includes(formData.visitType)
      ? t(`visitModal.form.visitTypes.${formData.visitType}`)
      : formData.visitType || '-';

  // D — the three date/time pairs
  const completionPairs = [
    { key: 'ecrf', icon: LuMonitorCheck, date: 'ecrfCompletionDate', time: 'ecrfCompletionTime' },
    { key: 'sourceDoc', icon: LuFileText, date: 'sourceDocCompletionDate', time: 'sourceDocCompletionTime' },
    { key: 'signature', icon: LuPenLine, date: 'signatureCompletionDate', time: 'signatureCompletionTime' },
  ];

  return (
    <div className="tw-space-y-4">
      <SectionCard {...section('A')} tone="emerald">
        <div className="tw-grid tw-grid-cols-1 tw-gap-4 sm:tw-grid-cols-2 xl:tw-grid-cols-3">
          <Field label={f('projectName')}><ReadValue>{projectTitle || formData.projectName || '-'}</ReadValue></Field>
          <Field label={f('centerId')}><ReadValue>{formData.centerId || formData.centerName || '-'}</ReadValue></Field>
          <Field label={f('visitType')}><ReadValue>{visitTypeText}</ReadValue></Field>
          <Field label={f('siteCoordinator')}><ReadValue>{formData.employeeName || '-'}</ReadValue></Field>
          <Field label={f('patientId')} htmlFor="vm-site-patientId">
            <TextInput
              id="vm-site-patientId"
              type="text"
              value={normalizePatientId(ev.patientId) || normalizePatientId(formData.patientId)}
              onChange={(event) => onEvalChange('patientId', event.target.value)}
              disabled={isReadOnly}
              placeholder={f('sitePatientPlaceholder')}
            />
          </Field>
          <Field label={f('visitDate')}><ReadValue>{formData.date || '-'}</ReadValue></Field>
          <Field label={f('visitStartTime')}><ReadValue>{formData.startTime || '-'}</ReadValue></Field>
        </div>
      </SectionCard>

      <SectionCard {...section('B')} tone="teal">
        <div className={FIELD_GRID}>
          <Field label={f('screenFail')} {...req('screenFail')}>
            <div id={fieldAnchor('screenFail')}>
              <YesNo label={f('screenFail')} value={ev.screenFail} onChange={(value) => onEvalChange('screenFail', value)} disabled={isReadOnly} />
            </div>
          </Field>
          <Field label={f('dropPatient')} {...req('dropPatient')}>
            <div id={fieldAnchor('dropPatient')}>
              <YesNo label={f('dropPatient')} value={ev.dropPatient} onChange={(value) => onEvalChange('dropPatient', value)} disabled={isReadOnly} />
            </div>
          </Field>
        </div>
      </SectionCard>

      <SectionCard {...section('C')} tone="amber">
        <div className="tw-space-y-4">
          <div className={FIELD_GRID}>
            <Field label={f('protocolDeviation')} {...req('protocolDeviation')}>
              <div id={fieldAnchor('protocolDeviation')}>
                <YesNo label={f('protocolDeviation')} value={ev.protocolDeviation} onChange={(value) => onEvalChange('protocolDeviation', value)} disabled={isReadOnly} />
              </div>
            </Field>
            {ev.protocolDeviation === 'yes' && (
              <Field label={f('capaOpened')} {...req('capaOpened')}>
                <div id={fieldAnchor('capaOpened')}>
                  <YesNo label={f('capaOpened')} value={ev.capaOpened} onChange={(value) => onEvalChange('capaOpened', value)} disabled={isReadOnly} />
                </div>
              </Field>
            )}
          </div>
          <Field label={f('comments')} htmlFor="vm-site-comments">
            <TextArea id="vm-site-comments" rows={3} value={ev.comments || ''} onChange={(event) => onEvalChange('comments', event.target.value)} disabled={isReadOnly} placeholder={f('commentsPlaceholder')} />
          </Field>
        </div>
      </SectionCard>

      <SectionCard id="vm-section-completion" {...section('D')} tone="sky">
        <div className="tw-space-y-4">
          {pendingCompletionLabels.length > 0 ? (
            <Notice tone="warning" title={t('visitModal.notices.pendingTitle', { items: pendingCompletionLabels.join(', ') })}>
              {t('visitModal.evaluation.optionalSection')}
            </Notice>
          ) : (
            <Notice tone="info">{t('visitModal.evaluation.optionalSection')}</Notice>
          )}

          <div className="tw-grid tw-grid-cols-1 tw-gap-3 lg:tw-grid-cols-3">
            {completionPairs.map(({ key, icon: Icon, date, time }) => (
              <div key={key} className={`${PANEL} tw-space-y-3 tw-p-3.5`}>
                <p className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-semibold tw-text-slate-800 dark:tw-text-slate-100">
                  <Icon aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-sky-500" />
                  {f(key)}
                </p>
                <div className="tw-grid tw-grid-cols-2 tw-gap-2 lg:tw-grid-cols-1 xl:tw-grid-cols-2">
                  <Field label={f('completionDate')} htmlFor={`vm-site-${date}`}>
                    <TextInput id={`vm-site-${date}`} type="date" value={ev[date] || ''} onChange={(event) => onEvalChange(date, event.target.value)} disabled={isReadOnly} />
                  </Field>
                  <Field label={f('completionTime')} htmlFor={`vm-site-${time}`}>
                    <TextInput id={`vm-site-${time}`} type="time" value={ev[time] || ''} onChange={(event) => onEvalChange(time, event.target.value)} disabled={isReadOnly} />
                  </Field>
                </div>
              </div>
            ))}
          </div>

          <div className={FIELD_GRID}>
            <Field label={f('sourceDocCompleted')}>
              <YesNo label={f('sourceDocCompleted')} value={ev.sourceDocCompleted} onChange={(value) => onEvalChange('sourceDocCompleted', value)} disabled={isReadOnly} />
            </Field>
            {ev.sourceDocCompleted === 'yes' && (
              <Field label={f('sourceDocCompletionTime')} htmlFor="vm-site-sourceDocCompletionTime-2">
                <TextInput id="vm-site-sourceDocCompletionTime-2" type="time" value={ev.sourceDocCompletionTime || ''} onChange={(event) => onEvalChange('sourceDocCompletionTime', event.target.value)} disabled={isReadOnly} />
              </Field>
            )}
          </div>
        </div>
      </SectionCard>

      <SectionCard {...section('E')} tone="violet">
        <div className="tw-space-y-4">
          <div className={GRID_3}>
            <Field label={f('processEndDate')} htmlFor={fieldAnchor('processEndDate')} {...req('processEndDate')}>
              <TextInput id={fieldAnchor('processEndDate')} type="date" value={ev.processEndDate || ''} onChange={(event) => onEvalChange('processEndDate', event.target.value)} disabled={isReadOnly} />
            </Field>
            <Field label={f('cycleTime')} hint={f('autoCalculated')}>
              <ReadValue tone={cycleTime !== null ? 'accent' : 'muted'} strong>
                {cycleTime !== null ? t('visitModal.common.days', { count: cycleTime }) : '-'}
              </ReadValue>
            </Field>
            <Field label={f('slaTarget')} htmlFor={fieldAnchor('slaTarget')} {...req('slaTarget')}>
              <TextInput id={fieldAnchor('slaTarget')} type="number" min="0" value={ev.slaTarget || ''} onChange={(event) => onEvalChange('slaTarget', event.target.value)} disabled={isReadOnly} placeholder={f('slaTargetPlaceholder')} />
            </Field>
          </div>
          <div className={FIELD_GRID}>
            <Field label={f('slaMet')} hint={f('autoCalculated')}>
              <ReadValue tone={slaMet === true ? 'success' : slaMet === false ? 'danger' : 'muted'} strong>{yesNo(slaMet)}</ReadValue>
            </Field>
            <Field label={f('slaOverdue')} hint={f('autoCalculated')}>
              <ReadValue tone={slaOverdue === true ? 'danger' : slaOverdue === false ? 'success' : 'muted'} strong>{yesNo(slaOverdue)}</ReadValue>
            </Field>
          </div>
        </div>
      </SectionCard>

      <SectionCard {...section('F')} tone="rose">
        <div className="tw-space-y-4">
          <div className={FIELD_GRID}>
            <Field label={f('issueOccurred')} {...req('issueOccurred')}>
              <div id={fieldAnchor('issueOccurred')}>
                <YesNo label={f('issueOccurred')} value={ev.issueOccurred} onChange={(value) => onEvalChange('issueOccurred', value)} disabled={isReadOnly} />
              </div>
            </Field>
            {ev.issueOccurred === 'yes' && (
              <Field label={f('issueCategory')} htmlFor={fieldAnchor('issueCategory')} {...req('issueCategory')}>
                <SelectInput
                  id={fieldAnchor('issueCategory')}
                  value={ev.issueCategory || ''}
                  onChange={(event) => onEvalChange('issueCategory', event.target.value)}
                  disabled={isReadOnly}
                  placeholder={t('visitModal.common.select')}
                  options={ISSUE_CATEGORIES.map((value) => ({ value, label: t(`visitModal.evaluation.issueCategories.${value}`) }))}
                />
              </Field>
            )}
          </div>
          {ev.issueOccurred === 'yes' && (
            <Field label={f('issueDetails')} htmlFor={fieldAnchor('issueDetails')} {...req('issueDetails')}>
              <TextArea id={fieldAnchor('issueDetails')} rows={3} value={ev.issueDetails || ''} onChange={(event) => onEvalChange('issueDetails', event.target.value)} disabled={isReadOnly} placeholder={f('issueDetailsPlaceholder')} />
            </Field>
          )}
        </div>
      </SectionCard>
    </div>
  );
};

SiteEvaluation.propTypes = {
  formData: PropTypes.object.isRequired,
  isReadOnly: PropTypes.bool,
  projectTitle: PropTypes.string,
  missingKeys: PropTypes.instanceOf(Set).isRequired,
  sections: PropTypes.object.isRequired,
  cycleTime: PropTypes.number,
  slaMet: PropTypes.bool,
  slaOverdue: PropTypes.bool,
  pendingCompletionLabels: PropTypes.arrayOf(PropTypes.string).isRequired,
  onEvalChange: PropTypes.func.isRequired,
};

export default SiteEvaluation;
